import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { usePageMeta } from '../../lib/usePageMeta'
import { useCaptainSession } from '../../lib/captainSession'
import {
  cancelCaptainSignup,
  changeCaptainRole,
  describeCaptainRpcError,
  getGroupBySlug,
  getMyClubCaptainRole,
  signupForCaptainRole,
  type PortalCaptainRole,
  type PortalGroup,
} from '../../lib/captainApi'
import { useCaptainPortalData } from '../../lib/useCaptainPortalData'
import {
  clearEventIdFromLocation,
  clearPendingEventId,
  matchDeepLink,
  resolveRequestedEventId,
  stashPendingEventId,
  STALE_DEEP_LINK_MESSAGE,
  type DeepLinkOutcome,
} from '../../lib/captainDeepLink'
import GrainOverlay from '../../components/captain/GrainOverlay'
import PortalShell from '../../components/captain/PortalShell'
import AuthGate from './AuthGate'
import CaptainView from './CaptainView'
import OrganizerView from './OrganizerView'
import type { CaptainRole, ClubInfo } from '../../types/captain'
import './captainPortal.css'

export default function CaptainPortal() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const { session, loading: authLoading, signIn, signInWithGoogle, signInWithApple, signOut } =
    useCaptainSession()

  // -------------------------------------------------------------------------
  // Deep link (ON-426): `/captain/:slug?event_id=<uuid>` from a Concierge
  // call-out. Captured once on mount — from the URL, or from the sessionStorage
  // stash left behind before an OAuth redirect (which drops the query string).
  // -------------------------------------------------------------------------
  const [requestedEventId] = useState<string | null>(() =>
    resolveRequestedEventId(slug, location.search)
  )
  // One-shot: consume the stash once mounted (not inside the initialiser —
  // StrictMode double-invokes it) so a later plain visit can't resurrect it.
  useEffect(() => {
    if (slug) clearPendingEventId(slug)
  }, [slug])
  const [deepLink, setDeepLink] = useState<DeepLinkOutcome>({ status: 'none' })
  const [deepLinkNoticeDismissed, setDeepLinkNoticeDismissed] = useState(false)

  // -------------------------------------------------------------------------
  // Group resolution
  // -------------------------------------------------------------------------
  const [group, setGroup] = useState<PortalGroup | null>(null)
  const [groupLoading, setGroupLoading] = useState(true)
  const [groupNotFound, setGroupNotFound] = useState(false)

  useEffect(() => {
    if (!slug) return
    let active = true
    setGroupLoading(true)
    setGroupNotFound(false)
    getGroupBySlug(slug)
      .then((g) => {
        if (!active) return
        if (!g) setGroupNotFound(true)
        else setGroup(g)
      })
      .catch(() => {
        if (active) setGroupNotFound(true)
      })
      .finally(() => {
        if (active) setGroupLoading(false)
      })
    return () => {
      active = false
    }
  }, [slug])

  // -------------------------------------------------------------------------
  // Role resolution (authenticated only)
  // -------------------------------------------------------------------------
  const [role, setRole] = useState<PortalCaptainRole | null>(null)
  const [roleLoading, setRoleLoading] = useState(false)

  useEffect(() => {
    if (!session || !group) {
      setRole(null)
      return
    }
    let active = true
    setRoleLoading(true)
    getMyClubCaptainRole(group.id)
      .then((r) => {
        if (active) setRole(r)
      })
      .catch(() => {
        // Per ON-245 spec, any authenticated user falls back to the captain
        // view if the role check fails.
        if (active) setRole('captain')
      })
      .finally(() => {
        if (active) setRoleLoading(false)
      })
    return () => {
      active = false
    }
  }, [session, group])

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------
  const data = useCaptainPortalData({
    groupId: group?.id,
    isOrganizer: role === 'organizer',
    userId: session?.user?.id,
    enabled: !!group && !!session && !!role,
  })

  const club: ClubInfo | null = group
    ? { id: group.id, slug: group.slug, name: group.name }
    : null

  usePageMeta(
    club
      ? {
          title: `Captains · ${club.name}`,
          description: `Volunteer as a captain for ${club.name}. Sign up via your Stazon account.`,
        }
      : null
  )

  // -------------------------------------------------------------------------
  // Mutation handlers
  // -------------------------------------------------------------------------
  const handleSignup = useCallback(
    async (eventId: string, signupRole: CaptainRole): Promise<string | null> => {
      try {
        await signupForCaptainRole(eventId, signupRole)
        await data.refetch()
        return null
      } catch (e) {
        return describeCaptainRpcError(e)
      }
    },
    [data]
  )

  const handleCancel = useCallback(
    async (signupId: string): Promise<string | null> => {
      try {
        await cancelCaptainSignup(signupId)
        await data.refetch()
        return null
      } catch (e) {
        return describeCaptainRpcError(e)
      }
    },
    [data]
  )

  const handleChangeRole = useCallback(
    async (signupId: string, newRole: CaptainRole): Promise<string | null> => {
      try {
        await changeCaptainRole(signupId, newRole)
        await data.refetch()
        return null
      } catch (e) {
        return describeCaptainRpcError(e)
      }
    },
    [data]
  )

  const handleSignIn = useCallback(
    (email: string, password: string) => signIn(email, password),
    [signIn]
  )

  // The OAuth redirectTo is origin + pathname only (see captainSession.ts), so
  // the `?event_id=` would be lost across the provider round-trip. Stash it
  // keyed by slug right before leaving; it is restored (one-shot) on remount.
  const stashDeepLinkForRedirect = useCallback(() => {
    if (slug && requestedEventId) stashPendingEventId(slug, requestedEventId)
  }, [slug, requestedEventId])

  // On success the provider call navigates away and the stash is consumed on
  // remount. If it fails *before* redirecting (returns an error string or
  // throws) we are still mounted with the stash written and the mount-time
  // clear already spent — drop it here, otherwise a later password sign-in
  // followed by a refresh would read the orphaned stash and re-open the modal.
  const signInViaProvider = useCallback(
    async (start: () => Promise<string | null>): Promise<string | null> => {
      stashDeepLinkForRedirect()
      let error: string | null
      try {
        error = await start()
      } catch (e) {
        if (slug) clearPendingEventId(slug)
        throw e
      }
      if (error && slug) clearPendingEventId(slug)
      return error
    },
    [slug, stashDeepLinkForRedirect]
  )
  const handleGoogleSignIn = useCallback(
    () => signInViaProvider(signInWithGoogle),
    [signInViaProvider, signInWithGoogle]
  )
  const handleAppleSignIn = useCallback(
    () => signInViaProvider(signInWithApple),
    [signInViaProvider, signInWithApple]
  )

  const handleSignOut = useCallback(() => signOut(), [signOut])

  // Resolve the deep link exactly once, the first time the needs-captains list
  // has loaded *successfully*. `hasLoaded` also flips on a failed fetch (with
  // an empty list), which would misreport a live event as stale and scrub the
  // param so it could never be matched after recovery — so wait out errors
  // and in-flight refetches (`loading` guards the window where `error` has
  // been reset but the list has not yet been replaced). Resolving on every
  // refetch would re-flag the event as stale after the volunteer signs up and
  // the list changes underneath them. Once resolved either way, the param is
  // scrubbed from the URL and any login stash is consumed so a refresh doesn't
  // re-open the modal.
  useEffect(() => {
    if (
      !requestedEventId ||
      !data.hasLoaded ||
      data.loading ||
      data.error ||
      deepLink.status !== 'none'
    ) {
      return
    }
    setDeepLink(matchDeepLink(requestedEventId, data.upcomingEvents))
    clearEventIdFromLocation()
    if (slug) clearPendingEventId(slug)
  }, [
    slug,
    requestedEventId,
    data.hasLoaded,
    data.loading,
    data.error,
    data.upcomingEvents,
    deepLink.status,
  ])

  // -------------------------------------------------------------------------
  // Loading / not-found gates
  // -------------------------------------------------------------------------
  if (groupLoading) {
    return <PortalLoading />
  }

  if (groupNotFound || !club) {
    return <PortalNotFound slug={slug ?? ''} />
  }

  // -------------------------------------------------------------------------
  // View routing
  // -------------------------------------------------------------------------
  if (authLoading) {
    return <PortalLoading />
  }

  if (!session) {
    return (
      <div className="cp-root">
        <GrainOverlay />
        <AuthGate
          club={club}
          onSignIn={handleSignIn}
          onGoogleSignIn={handleGoogleSignIn}
          onAppleSignIn={handleAppleSignIn}
        />
      </div>
    )
  }

  if (roleLoading || !role || !data.hasLoaded) {
    return <PortalLoading />
  }

  const userName =
    session.user?.user_metadata?.full_name ?? session.user?.email ?? 'Captain'
  const captainId = session.user?.id ?? ''
  const autoOpenEventId = deepLink.status === 'open' ? deepLink.eventId : null
  const staleNotice =
    deepLink.status === 'stale' && !deepLinkNoticeDismissed ? (
      <DeepLinkNotice
        message={STALE_DEEP_LINK_MESSAGE}
        onDismiss={() => setDeepLinkNoticeDismissed(true)}
      />
    ) : null

  return (
    <div className="cp-root">
      <GrainOverlay />

      {role === 'captain' ? (
        <PortalShell
          club={club}
          role="captain"
          userName={userName}
          onSignOut={handleSignOut}
        >
          {staleNotice}
          <CaptainView
            captainId={captainId}
            upcomingEvents={data.upcomingEvents}
            pastEvents={data.pastEvents}
            mySignupIdByEventId={data.mySignupIdByEventId}
            onSignup={handleSignup}
            onCancel={handleCancel}
            onChangeRole={handleChangeRole}
            autoOpenEventId={autoOpenEventId}
          />
        </PortalShell>
      ) : (
        <PortalShell
          club={club}
          role="organizer"
          userName={userName}
          onSignOut={handleSignOut}
        >
          {staleNotice}
          <OrganizerView
            captainId={captainId}
            events={data.upcomingEvents}
            pastEvents={data.pastEvents}
            mySignupIdByEventId={data.mySignupIdByEventId}
            onSignup={handleSignup}
            onCancel={handleCancel}
            onChangeRole={handleChangeRole}
            rostersByEventId={data.rostersByEventId}
            autoOpenEventId={autoOpenEventId}
          />
        </PortalShell>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Deep-link notice (ON-426) — non-blocking; the normal list renders beneath.
// ---------------------------------------------------------------------------

function DeepLinkNotice({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div
      role="status"
      data-deeplink-notice=""
      style={{
        marginBottom: 24,
        padding: '12px 16px',
        borderRadius: 8,
        background: 'rgba(194,229,48,0.06)',
        border: '0.5px solid rgba(194,229,48,0.3)',
        color: 'rgba(244,242,242,0.85)',
        fontFamily: "'Lora', Georgia, serif",
        fontSize: 14,
        lineHeight: 1.5,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span>
        {message} Here's everything that still needs a captain.
      </span>
      <button
        onClick={onDismiss}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(198,196,185,0.7)',
          cursor: 'pointer',
          fontSize: 14,
          padding: 4,
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading + not-found chrome
// ---------------------------------------------------------------------------

function PortalLoading() {
  return (
    <div className="cp-root">
      <GrainOverlay />
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: '2px solid rgba(244,242,242,0.12)',
            borderTopColor: '#C2E530',
            animation: 'spin 0.8s linear infinite',
          }}
          aria-label="Loading"
        />
      </div>
    </div>
  )
}

function PortalNotFound({ slug }: { slug: string }) {
  return (
    <div className="cp-root">
      <GrainOverlay />
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 40,
        }}
      >
        <div style={{ maxWidth: 440, textAlign: 'center' }}>
          <div
            style={{
              fontFamily:
                "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: '#EF606A',
              marginBottom: 18,
            }}
          >
            Club not found
          </div>
          <h1
            style={{
              fontFamily:
                "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontWeight: 700,
              fontSize: 32,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              color: '#F4F2F2',
              marginBottom: 18,
            }}
          >
            <span
              style={{
                fontFamily: "'Lora', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 400,
              }}
            >
              stazon.cc/captain/
            </span>
            <span style={{ color: '#C2E530' }}>{slug || '—'}</span>
          </h1>
          <p
            style={{
              fontFamily: "'Lora', Georgia, serif",
              fontSize: 15,
              lineHeight: 1.6,
              color: 'rgba(198,196,185,0.7)',
            }}
          >
            We couldn't find a Stazon club at this link. The address may be mistyped, or the club
            may have changed its handle. Ask your organizer for an up-to-date recruitment link.
          </p>
        </div>
      </div>
    </div>
  )
}
